/**
 * Pomodoro ve mola uyarısı (2026-10-27, kullanıcı kararı). React dışında tek durum; köşedeki küçük gösterge
 * components/PomodoroWidget.tsx (App kökünde).
 * - Pomodoro: 25 dk çalışma + 5 dk mola (dört pomodorodan sonra 15 dk). Plan görevinden ya da göstergeden başlar.
 *   Süre cihazda tutulur (`kpss_pomodoro_v1`), sayfa yenilense de devam eder.
 * - Mola uyarısı: pomodoro çalışmıyorken uygulamada kesintisiz 45 dk etkin kalınınca (son 2 dk'da dokunuş/tuş; 5 dk'lık
 *   boşluk "mola" sayılıp sayaç sıfırlanır) köşede "dinlen ya da 10 dk bu konunun oyununu oyna". Oyundayken uyarmaz.
 * - Ders bağlamı (MainApp hangi derste, oyunda mı): oyun düğmesi ve çalışma planının ders ders süre sayacı kullanır.
 */
import type { SubjectType } from '../types';

export type PomoPhase = 'idle' | 'focus' | 'break';
export interface PomoState {
    phase: PomoPhase;
    endsAt: number;
    /** Bugün biten pomodoro sayısı */
    count: number;
    day: string;
    label?: string;
    subject?: SubjectType;
    /** Gösterilecek uyarı: pomodoro bitti / mola bitti / 45 dk kesintisiz */
    alert?: 'focus-done' | 'break-done' | 'nudge';
}

export const FOCUS_MIN = 25;
export const BREAK_MIN = 5;
export const LONG_BREAK_MIN = 15;
export const NUDGE_MIN = 45;
const KEY = 'kpss_pomodoro_v1';
const OFF_KEY = 'kpss_pomodoro_nudge_off';
const IDLE_GAP_MS = 5 * 60 * 1000;
const ACTIVE_MS = 2 * 60 * 1000;
const SNOOZE_MS = 20 * 60 * 1000;

const today = () => { const d = new Date(); return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`; };

function load(): PomoState {
    try {
        const s = JSON.parse(localStorage.getItem(KEY) || 'null') as PomoState | null;
        if (s && s.phase) return s.day === today() ? s : { ...s, count: 0, day: today() };
    } catch { /* yok */ }
    return { phase: 'idle', endsAt: 0, count: 0, day: today() };
}

let state: PomoState = typeof window === 'undefined' ? { phase: 'idle', endsAt: 0, count: 0, day: '' } : load();
const listeners = new Set<(s: PomoState) => void>();
let ctx: { subject?: SubjectType; inGame?: boolean } = {};

function set(next: PomoState) {
    state = next;
    try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* yok */ }
    listeners.forEach(l => l(state));
}

export const getPomodoro = () => state;
export function subscribePomodoro(cb: (s: PomoState) => void): () => void {
    listeners.add(cb);
    cb(state);
    return () => { listeners.delete(cb); };
}

export function startFocus(label?: string, subject?: SubjectType) {
    resetActive();
    set({ ...state, phase: 'focus', endsAt: Date.now() + FOCUS_MIN * 60000, label, subject: subject || ctx.subject, alert: undefined });
}
export function startBreak(min?: number) {
    resetActive();
    const m = min ?? (state.count > 0 && state.count % 4 === 0 ? LONG_BREAK_MIN : BREAK_MIN);
    set({ ...state, phase: 'break', endsAt: Date.now() + m * 60000, alert: undefined });
}
export function stopPomodoro() { set({ ...state, phase: 'idle', endsAt: 0, alert: undefined }); }
export function dismissAlert() {
    if (state.alert === 'nudge') snoozeUntil = Date.now() + SNOOZE_MS;
    set({ ...state, alert: undefined });
}

/** MainApp: hangi derste, oyunda mı (oyunda mola uyarısı gelmez) */
export function setStudyContext(c: { subject?: SubjectType; inGame?: boolean } | null) { ctx = c || {}; }
export const getStudyContext = () => ctx;

export const isNudgeOff = () => { try { return localStorage.getItem(OFF_KEY) === '1'; } catch { return false; } };
export function setNudgeOff(off: boolean) {
    try { if (off) localStorage.setItem(OFF_KEY, '1'); else localStorage.removeItem(OFF_KEY); } catch { /* yok */ }
    if (off && state.alert === 'nudge') set({ ...state, alert: undefined });
}

let activeSince = Date.now();
let lastInput = Date.now();
let snoozeUntil = 0;
function resetActive() { activeSince = Date.now(); }

function vibrate() { try { navigator.vibrate?.([120, 80, 120]); } catch { /* yok */ } }

let started = false;
/** App açılışında bir kez */
export function startPomodoroClock() {
    if (started || typeof window === 'undefined') return;
    started = true;
    const bump = () => {
        const now = Date.now();
        if (now - lastInput > IDLE_GAP_MS) activeSince = now; // uzun boşluk = mola verdi
        lastInput = now;
    };
    ['pointerdown', 'keydown', 'wheel', 'touchstart'].forEach(ev => window.addEventListener(ev, bump, { passive: true, capture: true }));
    window.setInterval(() => {
        const now = Date.now();
        if (state.day !== today()) set({ ...state, count: 0, day: today() });
        if (state.phase === 'focus' && now >= state.endsAt) {
            set({ ...state, phase: 'idle', endsAt: 0, count: state.count + 1, alert: 'focus-done' });
            // Çalışma planı hedefi: biten pomodoro study_time.pDD'ye yazılır (studyPlanService › installStudySignals)
            try { window.dispatchEvent(new CustomEvent('kpss-pomodoro-done')); } catch { /* yok */ }
            vibrate();
            return;
        }
        if (state.phase === 'break' && now >= state.endsAt) {
            set({ ...state, phase: 'idle', endsAt: 0, alert: 'break-done' });
            resetActive();
            vibrate();
            return;
        }
        if (state.phase !== 'idle' || state.alert || isNudgeOff() || ctx.inGame) return;
        const visible = document.visibilityState === 'visible';
        if (visible && now - lastInput < ACTIVE_MS && now - activeSince >= NUDGE_MIN * 60000 && now >= snoozeUntil) {
            set({ ...state, alert: 'nudge' });
        }
    }, 5000);
}
